/**
 * Document corner detection + perspective flattening + basic adjustments.
 * Ported from the working `sohozkaj-site` image flatten tool logic.
 *
 * Notes:
 * - All operations are done on the client using Canvas.
 * - `flattenPerspective` outputs a JPEG/PNG/WebP blob via `canvas.toBlob`.
 */

const HANDLE_RADIUS = 8;

function gaussianElimination(A, b) {
    const n = b.length;
    const aug = A.map((row, i) => [...row, b[i]]);

    for (let col = 0; col < n; col++) {
        let maxRow = col;
        for (let row = col + 1; row < n; row++) {
            if (Math.abs(aug[row][col]) > Math.abs(aug[maxRow][col])) maxRow = row;
        }

        [aug[col], aug[maxRow]] = [aug[maxRow], aug[col]];
        if (Math.abs(aug[col][col]) < 1e-12) continue;

        for (let row = col + 1; row < n; row++) {
            const f = aug[row][col] / aug[col][col];
            for (let k = col; k <= n; k++) aug[row][k] -= f * aug[col][k];
        }
    }

    const x = new Array(n).fill(0);
    for (let i = n - 1; i >= 0; i--) {
        let sum = aug[i][n];
        for (let j = i + 1; j < n; j++) sum -= aug[i][j] * x[j];
        x[i] = sum / aug[i][i];
    }
    return x;
}

export function computeHomography(src, dst) {
    const A = [];
    const b = [];

    for (let i = 0; i < 4; i++) {
        const { x, y } = src[i];
        const { x: xp, y: yp } = dst[i];

        A.push([x, y, 1, 0, 0, 0, -xp * x, -xp * y]);
        b.push(xp);
        A.push([0, 0, 0, x, y, 1, -yp * x, -yp * y]);
        b.push(yp);
    }

    // h[8] is forced to 1 in the original implementation.
    return [...gaussianElimination(A, b), 1];
}

export function applyH(h, x, y) {
    const w = h[6] * x + h[7] * y + h[8];
    return { x: (h[0] * x + h[1] * y + h[2]) / w, y: (h[3] * x + h[4] * y + h[5]) / w };
}

export function getOutputDimensions(nc) {
    const [tl, tr, br, bl] = nc;
    const w = Math.round((Math.hypot(tr.x - tl.x, tr.y - tl.y) + Math.hypot(br.x - bl.x, br.y - bl.y)) / 2);
    const h = Math.round((Math.hypot(bl.x - tl.x, bl.y - tl.y) + Math.hypot(br.x - tr.x, br.y - tr.y)) / 2);
    const MAX = 2000;
    const scale = Math.min(1, MAX / Math.max(w, h, 1));
    return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

export function detectDocumentCorners(imgEl, nw, nh) {
    if (nw < 50 || nh < 50) return null;

    const MAXDIM = 400;
    const sc = Math.min(1, MAXDIM / Math.max(nw, nh));
    const dw = Math.round(nw * sc);
    const dh = Math.round(nh * sc);

    const cvs = document.createElement("canvas");
    cvs.width = dw;
    cvs.height = dh;
    cvs.getContext("2d").drawImage(imgEl, 0, 0, dw, dh);

    const { data } = cvs.getContext("2d").getImageData(0, 0, dw, dh);

    // Grayscale
    const gray = new Uint8Array(dw * dh);
    for (let i = 0; i < dw * dh; i++) gray[i] = (77 * data[i * 4] + 150 * data[i * 4 + 1] + 29 * data[i * 4 + 2]) >> 8;

    // Separable Gaussian blur
    const gaussianBlurSep = (src, w, h, sigma) => {
        const half = Math.ceil(2.5 * sigma);
        const len = 2 * half + 1;

        const k = new Float32Array(len);
        let kSum = 0;
        for (let i = 0; i < len; i++) {
            const x = i - half;
            k[i] = Math.exp((-x * x) / (2 * sigma * sigma));
            kSum += k[i];
        }
        for (let i = 0; i < len; i++) k[i] /= kSum;

        const tmp = new Float32Array(w * h);
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                let s = 0;
                for (let j = 0; j < len; j++) s += src[y * w + Math.max(0, Math.min(w - 1, x + j - half))] * k[j];
                tmp[y * w + x] = s;
            }
        }

        const out = new Float32Array(w * h);
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                let s = 0;
                for (let j = 0; j < len; j++) s += tmp[Math.max(0, Math.min(h - 1, y + j - half)) * w + x] * k[j];
                out[y * w + x] = s;
            }
        }

        return out;
    };

    const blurred = gaussianBlurSep(gray, dw, dh, 2);

    // Otsu threshold
    const hist = new Int32Array(256);
    for (let i = 0; i < blurred.length; i++) hist[Math.min(255, blurred[i] | 0)]++;

    let wB = 0;
    let sumB = 0;
    let sumAll = 0;
    let maxVar = 0;
    let thresh = 128;

    for (let t = 0; t < 256; t++) sumAll += t * hist[t];

    for (let t = 0; t < 256; t++) {
        wB += hist[t];
        if (!wB) continue;
        const wF = dw * dh - wB;
        if (!wF) break;
        sumB += t * hist[t];

        const mB = sumB / wB;
        const mF = (sumAll - sumB) / wF;
        const v = wB * wF * (mB - mF) ** 2;
        if (v > maxVar) {
            maxVar = v;
            thresh = t;
        }
    }

    // If the two classes look too similar, can't distinguish doc from background.
    if (maxVar < dw * dh * 30) return null;

    const mask = new Uint8Array(dw * dh);
    for (let i = 0; i < blurred.length; i++) mask[i] = blurred[i] > thresh ? 1 : 0;

    // If the centre is mostly dark, invert.
    let cFore = 0;
    const cx0 = Math.round(dw * 0.3);
    const cx1 = Math.round(dw * 0.7);
    const cy0 = Math.round(dh * 0.3);
    const cy1 = Math.round(dh * 0.7);
    for (let y = cy0; y < cy1; y++) for (let x = cx0; x < cx1; x++) cFore += mask[y * dw + x];
    if (cFore < (cx1 - cx0) * (cy1 - cy0) * 0.4) for (let i = 0; i < mask.length; i++) mask[i] ^= 1;

    // Boundary scanning
    const MR = Math.round(Math.min(dw, dh) * 0.03);
    const leftPts = [];
    const rightPts = [];
    const topPts = [];
    const botPts = [];

    for (let y = MR; y < dh - MR; y++) {
        let left = -1;
        let right = -1;
        for (let x = 0; x < dw; x++) if (mask[y * dw + x]) {
            left = x;
            break;
        }
        for (let x = dw - 1; x >= 0; x--) if (mask[y * dw + x]) {
            right = x;
            break;
        }

        if (left > MR) leftPts.push([left, y]);
        if (right >= 0 && right < dw - MR) rightPts.push([right, y]);
    }

    for (let x = MR; x < dw - MR; x++) {
        let top = -1;
        let bot = -1;
        for (let y = 0; y < dh; y++) if (mask[y * dw + x]) {
            top = y;
            break;
        }
        for (let y = dh - 1; y >= 0; y--) if (mask[y * dw + x]) {
            bot = y;
            break;
        }

        if (top > MR) topPts.push([x, top]);
        if (bot >= 0 && bot < dh - MR) botPts.push([x, bot]);
    }

    if (leftPts.length < 5 || rightPts.length < 5 || topPts.length < 5 || botPts.length < 5) return null;

    // TLS line fitting helper.
    const fitLineTLS = (pts) => {
        const n = pts.length;
        let sx = 0;
        let sy = 0;
        for (const [x, y] of pts) {
            sx += x;
            sy += y;
        }
        const mx = sx / n;
        const my = sy / n;

        let cxx = 0;
        let cxy = 0;
        let cyy = 0;
        for (const [x, y] of pts) {
            const dx = x - mx;
            const dy = y - my;
            cxx += dx * dx;
            cxy += dx * dy;
            cyy += dy * dy;
        }

        const tr = cxx + cyy;
        const det = cxx * cyy - cxy * cxy;
        const lam = tr / 2 - Math.sqrt(Math.max(0, (tr * tr) / 4 - det));
        let a = -cxy;
        let b = cxx - lam;

        const len = Math.hypot(a, b);
        if (len < 1e-10) return null;
        a /= len;
        b /= len;
        return { a, b, c: -(a * mx + b * my) };
    };

    const ransacLine = (pts) => {
        const ITERS = 100;
        const DIST = 5;
        let best = null;
        let bestCount = 0;

        for (let iter = 0; iter < ITERS; iter++) {
            const i1 = (Math.random() * pts.length) | 0;
            let i2 = (Math.random() * pts.length) | 0;
            while (i2 === i1) i2 = (Math.random() * pts.length) | 0;

            const [x1, y1] = pts[i1];
            const [x2, y2] = pts[i2];
            const d = Math.hypot(x2 - x1, y2 - y1);
            if (d < 2) continue;

            const a = (y2 - y1) / d;
            const b = -(x2 - x1) / d;
            const c = -(a * x1 + b * y1);

            let count = 0;
            for (const [px, py] of pts) if (Math.abs(a * px + b * py + c) < DIST) count++;

            if (count > bestCount) {
                bestCount = count;
                best = { a, b, c };
            }
        }

        if (!best || bestCount < Math.max(5, pts.length * 0.3)) return null;
        const inliers = pts.filter(([px, py]) => Math.abs(best.a * px + best.b * py + best.c) < DIST);
        return fitLineTLS(inliers);
    };

    const lineLeft = ransacLine(leftPts);
    const lineRight = ransacLine(rightPts);
    const lineTop = ransacLine(topPts);
    const lineBot = ransacLine(botPts);
    if (!lineLeft || !lineRight || !lineTop || !lineBot) return null;

    // Intersect pairs of lines -> 4 corners.
    const intersect = (l1, l2) => {
        const det = l1.a * l2.b - l2.a * l1.b;
        if (Math.abs(det) < 1e-6) return null;
        return {
            x: (-l1.c * l2.b + l2.c * l1.b) / det,
            y: (-l1.a * l2.c + l2.a * l1.c) / det,
        };
    };

    const tl = intersect(lineTop, lineLeft);
    const tr = intersect(lineTop, lineRight);
    const br = intersect(lineBot, lineRight);
    const bl = intersect(lineBot, lineLeft);
    if (!tl || !tr || !br || !bl) return null;

    const area = Math.abs(
        tl.x * (tr.y - bl.y) +
        tr.x * (br.y - tl.y) +
        br.x * (bl.y - tr.y) +
        bl.x * (tl.y - br.y),
    ) / 2;

    if (area < dw * dh * 0.05) return null;

    // Clamp & scale back to natural image coords.
    const C = 20;
    const clamp = (p) => ({
        x: Math.max(-C, Math.min(dw + C, p.x)) / sc,
        y: Math.max(-C, Math.min(dh + C, p.y)) / sc,
    });

    // Return TL, TR, BR, BL
    return [clamp(tl), clamp(tr), clamp(br), clamp(bl)];
}

/**
 * Perspective flatten: runs inverse homography sampling and outputs a blob.
 */
export async function flattenPerspective(imgUrl, naturalSize, rotation, naturalCorners, outW, outH, mimeType) {
    const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = imgUrl;
    });

    const nw = naturalSize.width;
    const nh = naturalSize.height;
    const rad = (rotation * Math.PI) / 180;

    const sw = Math.ceil(Math.abs(nw * Math.cos(rad)) + Math.abs(nh * Math.sin(rad)));
    const sh = Math.ceil(Math.abs(nw * Math.sin(rad)) + Math.abs(nh * Math.cos(rad)));

    const srcCanvas = document.createElement("canvas");
    srcCanvas.width = sw;
    srcCanvas.height = sh;
    const srcCtx = srcCanvas.getContext("2d");

    srcCtx.translate(sw / 2, sh / 2);
    srcCtx.rotate(rad);
    srcCtx.drawImage(img, -nw / 2, -nh / 2, nw, nh);

    const srcData = srcCtx.getImageData(0, 0, sw, sh).data;

    // Destination corners are a simple axis-aligned rectangle.
    const dst = [{ x: 0, y: 0 }, { x: outW, y: 0 }, { x: outW, y: outH }, { x: 0, y: outH }];
    const Hinv = computeHomography(dst, naturalCorners);

    const dstCanvas = document.createElement("canvas");
    dstCanvas.width = outW;
    dstCanvas.height = outH;
    const dstCtx = dstCanvas.getContext("2d");

    const dstImgData = dstCtx.createImageData(outW, outH);
    const dd = dstImgData.data;

    for (let oy = 0; oy < outH; oy++) {
        for (let ox = 0; ox < outW; ox++) {
            const { x: sx, y: sy } = applyH(Hinv, ox, oy);

            const x0 = Math.floor(sx);
            const y0 = Math.floor(sy);
            const x1 = x0 + 1;
            const y1 = y0 + 1;

            const fx = sx - x0;
            const fy = sy - y0;

            const di = (oy * outW + ox) * 4;

            // Outside -> solid white.
            if (x0 < 0 || y0 < 0 || x1 >= sw || y1 >= sh) {
                dd[di] = 255;
                dd[di + 1] = 255;
                dd[di + 2] = 255;
                dd[di + 3] = 255;
                continue;
            }

            const i00 = (y0 * sw + x0) * 4;
            const i10 = (y0 * sw + x1) * 4;
            const i01 = (y1 * sw + x0) * 4;
            const i11 = (y1 * sw + x1) * 4;

            // Bilinear interpolation weights.
            const a = (1 - fx) * (1 - fy);
            const b2 = fx * (1 - fy);
            const c = (1 - fx) * fy;
            const d2 = fx * fy;

            dd[di] = Math.round(srcData[i00] * a + srcData[i10] * b2 + srcData[i01] * c + srcData[i11] * d2);
            dd[di + 1] = Math.round(srcData[i00 + 1] * a + srcData[i10 + 1] * b2 + srcData[i01 + 1] * c + srcData[i11 + 1] * d2);
            dd[di + 2] = Math.round(srcData[i00 + 2] * a + srcData[i10 + 2] * b2 + srcData[i01 + 2] * c + srcData[i11 + 2] * d2);

            // Output alpha = 255 (same as production).
            dd[di + 3] = 255;
        }
    }

    dstCtx.putImageData(dstImgData, 0, 0);

    return new Promise((resolve) => dstCanvas.toBlob(resolve, mimeType, 0.95));
}

/**
 * Sharpens via luminance unsharp mask (preserves hue/saturation; reduces color fringing).
 */
export function applySharpening(srcCanvas, amount) {
    if (amount < 0.01) return srcCanvas;

    const W = srcCanvas.width;
    const H = srcCanvas.height;

    const blurCanvas = document.createElement("canvas");
    blurCanvas.width = W;
    blurCanvas.height = H;
    const blurCtx = blurCanvas.getContext("2d");
    blurCtx.filter = "blur(1.5px)";
    blurCtx.drawImage(srcCanvas, 0, 0);

    const src = srcCanvas.getContext("2d").getImageData(0, 0, W, H).data;
    const blur = blurCtx.getImageData(0, 0, W, H).data;

    const out = document.createElement("canvas");
    out.width = W;
    out.height = H;
    const outCtx = out.getContext("2d");

    const id = outCtx.createImageData(W, H);
    const od = id.data;

    for (let i = 0; i < W * H * 4; i += 4) {
        const lSrc = 0.299 * src[i] + 0.587 * src[i + 1] + 0.114 * src[i + 2];
        const lBlur = 0.299 * blur[i] + 0.587 * blur[i + 1] + 0.114 * blur[i + 2];
        const delta = amount * (lSrc - lBlur);

        od[i] = Math.min(255, Math.max(0, src[i] + delta));
        od[i + 1] = Math.min(255, Math.max(0, src[i + 1] + delta));
        od[i + 2] = Math.min(255, Math.max(0, src[i + 2] + delta));
        od[i + 3] = src[i + 3]; // preserve alpha
    }

    outCtx.putImageData(id, 0, 0);
    return out;
}

export function applyBrightnessContrast(srcCanvas, brightness, contrast) {
    if (brightness === 100 && contrast === 100) return srcCanvas;

    const out = document.createElement("canvas");
    out.width = srcCanvas.width;
    out.height = srcCanvas.height;
    const ctx = out.getContext("2d");
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
    ctx.drawImage(srcCanvas, 0, 0);
    ctx.filter = "none";
    return out;
}

export { HANDLE_RADIUS };

